import React, { useMemo, useState } from 'react';
import { Canvas, ThreeEvent } from '@react-three/fiber';
import { OrbitControls, Text, Html, Billboard } from '@react-three/drei';
import * as THREE from 'three';
import { ThreeDBarConfig, ThreeDBarDatum } from './config/threeDBarTypes';
import { useThreeDBarControls } from './hooks/useThreeDBarControls';

interface ThreeDBarCanvasProps {
    data: ThreeDBarDatum[];
    config: ThreeDBarConfig;
    xCategories: string[];
    zCategories: string[];
    seriesNames: string[];
    yScale: { max: number; type: string };
    onBarClick?: (datum: ThreeDBarDatum) => void;
}

const Bar = ({
    position,
    size,
    color,
    datum,
    config,
    onClick,
    onHover
}: {
    position: [number, number, number];
    size: [number, number, number];
    color: string;
    datum: ThreeDBarDatum;
    config: ThreeDBarConfig;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
    onHover: (hovering: boolean, e: ThreeEvent<PointerEvent>) => void;
}) => {
    const [hovered, setHovered] = useState(false);

    // Gold highlight color
    const highlightColor = '#F59E0B';
    const finalColor = hovered && config.enableHover ? highlightColor : color;

    return (
        <group position={position}>
            <mesh
                onClick={onClick}
                onPointerOver={(e) => {
                    e.stopPropagation();
                    setHovered(true);
                    onHover(true, e);
                }}
                onPointerOut={(e) => {
                    setHovered(false);
                    onHover(false, e);
                }}
            >
                <boxGeometry args={size} />
                <meshStandardMaterial
                    color={finalColor}
                    transparent
                    opacity={config.barOpacity}
                    roughness={0.3}
                    metalness={0.2}
                />
                {/* Optional Edge Highlight */}
                {hovered && config.enableHover && (
                    <lineSegments>
                        <edgesGeometry args={[new THREE.BoxGeometry(...size)]} />
                        <lineBasicMaterial color="white" linewidth={2} />
                    </lineSegments>
                )}
                {/* Value Label */}
                {config.showValueLabels && (
                    <Billboard
                        position={[0, size[1] / 2 + 1, 0]}
                        follow={true}
                        lockX={false}
                        lockY={false}
                        lockZ={false}
                    >
                        <Text
                            fontSize={config.xLabelFontSize / 15}
                            color="white"
                            anchorX="center"
                            anchorY="bottom"
                        >
                            {datum.value.toFixed(1)}
                        </Text>
                    </Billboard>
                )}
            </mesh>
        </group>
    );
};

const Scene = ({ data, config, xCategories, zCategories, seriesNames, yScale, onBarClick }: ThreeDBarCanvasProps) => {
    const cellSize = 25; // Increased from 10 to 25 to fix "too tight" labels
    const maxDim = Math.max(xCategories.length, zCategories.length);
    const sceneSize = maxDim * cellSize;
    const { controlsRef } = useThreeDBarControls(config, sceneSize);
    const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; content: any } | null>(null);

    // Layout Constants
    const barWidthBase = 15; // Increased from 8 to 15 to match new cell size

    // Calculate Layout
    const bars = useMemo(() => {
        const renderedBars: any[] = [];
        const xCount = xCategories.length;
        const zCount = zCategories.length || 1;

        // Center the grid
        const xOffset = -(xCount * cellSize) / 2;
        const zOffset = -(zCount * cellSize) / 2;

        // Helper to get coordinates
        const getCoords = (xCat: string, zCat?: string) => {
            const xi = xCategories.indexOf(xCat);
            const zi = zCat ? zCategories.indexOf(zCat) : 0;
            return {
                x: xOffset + xi * cellSize + cellSize / 2,
                z: zOffset + zi * cellSize + cellSize / 2
            };
        };

        // Grouping/Stacking Logic
        // We need to group data by X/Z cell first
        const cellMap = new Map<string, ThreeDBarDatum[]>();
        data.forEach(d => {
            const key = `${d.xCategory}::${d.zCategory || ''}`;
            if (!cellMap.has(key)) cellMap.set(key, []);
            cellMap.get(key)!.push(d);
        });

        cellMap.forEach((cellData, key) => {
            const { x, z } = getCoords(cellData[0].xCategory, cellData[0].zCategory);

            // Log Scale Helper
            const getHeight = (val: number) => {
                if (val <= 0) return 0;
                if (yScale.type === 'log') {
                    const logMax = Math.log(yScale.max || 1);
                    const logVal = Math.log(val);
                    if (logVal <= 0) return 0.1; // Min height for visibility
                    return (logVal / logMax) * 20;
                }
                return (val / yScale.max) * 20;
            };

            // Bar Width with Gap
            // In single/stacked, barGap controls padding (0 to 1)
            // In grouped, it controls gap between bars
            const baseWidth = barWidthBase * (1 - (config.mode === 'grouped' ? 0 : (config.barGap || 0.1)));

            if (config.mode === 'stacked') {
                let currentY = 0;
                cellData.forEach(d => {
                    const height = getHeight(d.value);
                    if (height <= 0) return;

                    const yPos = currentY + height / 2;
                    renderedBars.push({
                        ...d,
                        position: [x, yPos, z],
                        size: [baseWidth, height, baseWidth],
                        color: d.color || config.singleColor
                    });
                    currentY += height;
                });
            } else if (config.mode === 'grouped') {
                const count = cellData.length;
                const totalWidth = barWidthBase * 0.8;
                const widthPerBar = (totalWidth - (config.barGap * (count - 1))) / count;
                const startX = x - totalWidth / 2 + widthPerBar / 2;

                cellData.forEach((d, i) => {
                    const height = getHeight(d.value);
                    if (height <= 0) return;

                    const xPos = startX + i * (widthPerBar + config.barGap);

                    renderedBars.push({
                        ...d,
                        position: [xPos, height / 2, z],
                        size: [widthPerBar, height, barWidthBase * 0.8],
                        color: d.color || config.singleColor
                    });
                });
            } else {
                // Single mode
                const d = cellData[0];
                const height = getHeight(d.value);
                renderedBars.push({
                    ...d,
                    position: [x, height / 2, z],
                    size: [baseWidth, height, baseWidth],
                    color: d.color || config.singleColor
                });
            }
        });

        return renderedBars;
    }, [data, config, xCategories, zCategories, yScale]);

    return (
        <>
            <OrbitControls ref={controlsRef} makeDefault />

            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 20, 10]} intensity={1} castShadow />
            <pointLight position={[-10, 10, -10]} intensity={0.5} color="#F59E0B" />

            {/* Grid / Floor */}
            {config.showGrid && (
                <gridHelper
                    args={[Math.max(xCategories.length, zCategories.length) * cellSize * 2, Math.max(xCategories.length, zCategories.length) * 2]}
                    position={[0, 0, 0]}
                />
            )}

            {/* Axes Labels */}
            {config.showXLabels && xCategories.map((cat, i) => (
                <Text
                    key={`x-${cat}`}
                    position={[
                        -(xCategories.length * cellSize) / 2 + i * cellSize + cellSize / 2,
                        0.5,
                        (zCategories.length * cellSize) / 2 + 6 // Moved out further
                    ]}
                    fontSize={config.xLabelFontSize / 3} // Significantly increased size (was / 15)
                    color="white"
                    anchorX="center"
                    anchorY="top"
                    rotation={[-Math.PI / 2, 0, config.xLabelAngle * Math.PI / 180]}
                >
                    {cat}
                </Text>
            ))}

            {config.showZLabels && zCategories.map((cat, i) => (
                <Text
                    key={`z-${cat}`}
                    position={[
                        (xCategories.length * cellSize) / 2 + 6, // Moved out further
                        0.5,
                        -(zCategories.length * cellSize) / 2 + i * cellSize + cellSize / 2
                    ]}
                    fontSize={config.zLabelFontSize / 3} // Significantly increased size
                    color="white"
                    anchorX="left"
                    anchorY="middle"
                    rotation={[-Math.PI / 2, 0, 0]} // Keep flat, but maybe user wants them standing? Flat is standard for 3D floor.
                >
                    {cat}
                </Text>
            ))}

            {/* Bars */}
            {bars.map((bar, i) => (
                <Bar
                    key={bar.id}
                    position={bar.position}
                    size={bar.size}
                    color={bar.color}
                    datum={bar}
                    config={config}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (config.enableSelection && onBarClick) onBarClick(bar);
                    }}
                    onHover={(hovering, e) => {
                        if (hovering && config.enableHover) {
                            setTooltip({
                                visible: true,
                                x: e.clientX,
                                y: e.clientY,
                                content: bar
                            });
                        } else {
                            setTooltip(null);
                        }
                    }}
                />
            ))}

            {/* Tooltip (HTML Overlay) */}
            {tooltip && tooltip.visible && (
                <Html position={[0, 0, 0]} style={{ pointerEvents: 'none', display: 'none' }}>
                    {/* We use a fixed overlay instead of 3D positioned HTML to avoid occlusion issues, 
                but Html component follows 3D position. 
                For now, let's just use a portal or standard React state lifted up.
                Actually, standard HTML overlay in parent is better.
            */}
                </Html>
            )}
        </>
    );
};

export const ThreeDBarCanvas: React.FC<ThreeDBarCanvasProps> = (props) => {
    const cellSize = 25; // Increased to match Scene
    const maxDim = Math.max(props.xCategories.length, props.zCategories.length);
    const sceneSize = maxDim * cellSize;
    const initialDistance = Math.max(20, sceneSize * 1.5);

    return (
        <div className="w-full h-full relative">
            <Canvas shadows camera={{ position: [initialDistance, initialDistance, initialDistance], fov: 50, far: 50000 }}>
                <Scene {...props} />
            </Canvas>
            {/* Tooltip Overlay could go here if we lift state */}
        </div>
    );
};
